import React, { useState } from 'react';
import { ShieldCheck, ExternalLink } from 'lucide-react';
import { LazyArticlesCarouselWrapper } from './LazyComponents';
import LetterKeyInfo from './LetterKeyInfo';
import MentionsLegalesModal from './MentionsLegalesModal';
import { useAppContext } from '../store';

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
  const { state } = useAppContext();
  const { darkMode } = state.ui;
  const currentYear = new Date().getFullYear();
  const [isMentionsOpen, setIsMentionsOpen] = useState(false);

  return (
    <footer className={`mt-16 ${className}`}>
      <div>
        <LetterKeyInfo darkMode={darkMode} />
        <LazyArticlesCarouselWrapper />
      </div>

      <div className={darkMode ? 'bg-appines-petrol text-appines-white' : 'bg-[rgba(1,42,54,0.04)] text-appines-black'}>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="/logo-appines.png"
                  alt="Logo App'Ines"
                  className="h-8 w-8 rounded-full object-cover"
                />
                <span className={`text-lg font-semibold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>App'Ines</span>
              </div>
              <p className={`text-sm leading-relaxed mb-5 ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>
                L'app de recrutement en libéral et salariat des pros de santé.
              </p>
              <a
                href="https://appines.fr"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 font-medium text-sm transition-colors ${
                  darkMode ? 'text-appines-emerald hover:text-appines-white' : 'text-appines-petrol hover:text-appines-emerald'
                }`}
              >
                appines.fr
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            <div>
              <h3 className={`text-base font-semibold mb-5 ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Bon à savoir</h3>
              <div className="flex gap-3">
                <ShieldCheck className="w-5 h-5 text-appines-emerald flex-shrink-0 mt-0.5" />
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>
                  Les cotations et simulations proposées s'appuient sur les textes
                  officiels NGAP en vigueur. Elles sont fournies à titre indicatif pour
                  t'aider à anticiper l'impact des revalorisations sur ton activité. Pour
                  toute question spécifique à ta facturation, rapproche-toi de ta caisse
                  primaire ou d'un expert-comptable spécialisé santé.
                </p>
              </div>
            </div>

            <div>
              <h3 className={`text-base font-semibold mb-5 ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Informations légales</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <button
                    type="button"
                    onClick={() => setIsMentionsOpen(true)}
                    className={`transition-colors text-left ${
                      darkMode ? 'text-appines-white/65 hover:text-appines-emerald' : 'text-appines-petrol/65 hover:text-appines-petrol'
                    }`}
                  >
                    Mentions légales
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className={`mt-10 pt-6 border-t text-center text-sm ${
            darkMode ? 'border-appines-white/10 text-appines-white/45' : 'border-appines-petrol/12 text-appines-petrol/65'
          }`}>
            &copy; {currentYear}{' '}
            <a
              href="https://appines.fr"
              target="_blank"
              rel="noopener noreferrer"
              className={`transition-colors ${
                darkMode ? 'text-appines-white/80 hover:text-appines-emerald' : 'text-appines-petrol hover:text-appines-emerald'
              }`}
            >
              App'Ines
            </a>
            . Tous droits réservés.
          </div>
        </div>
      </div>

      <MentionsLegalesModal
        isOpen={isMentionsOpen}
        onClose={() => setIsMentionsOpen(false)}
      />
    </footer>
  );
};

export default Footer;
